/*
 * ─── Ridge ───
 *
 * The hero's landscape: a pale sun low behind four layered ridgelines in graded warm
 * greys, far ones pale and tall, near ones darker and lower, with mist between them.
 * Generated deterministically so server and client agree. The noise helpers are shared
 * with Terrain.jsx.
 */
export function rand(seed) {
  let s = seed >>> 0
  return () => {
    s = (s + 0x6d2b79f5) >>> 0
    let t = s
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/* Value noise with smooth interpolation. */
export function makeNoise(seed) {
  const r = rand(seed)
  const table = Array.from({ length: 512 }, () => r())
  return (x) => {
    const i = Math.floor(x)
    const f = x - i
    const u = f * f * (3 - 2 * f)
    const a = table[i & 511]
    const b = table[(i + 1) & 511]
    return a + (b - a) * u
  }
}

/* Ridged fractal: |noise| folded so peaks are sharp and valleys are soft. */
export function ridgeHeight(noise, x, octaves, sharp) {
  let amp = 1
  let freq = 1
  let sum = 0
  let norm = 0
  for (let o = 0; o < octaves; o++) {
    const n = noise(x * freq + o * 37.1) * 2 - 1
    const ridged = 1 - Math.abs(n)
    sum += Math.pow(ridged, sharp) * amp
    norm += amp
    amp *= 0.42
    freq *= 2.1
  }
  return sum / norm
}

/*
 * A real sun for the auth pages: an ember disc with a soft glow, drawn in canvas units so it
 * stays locked to the ridgelines. Place it before the ridge layers so the peaks silhouette
 * against it. Colours come from the --trodo-primary tokens.
 */
export function SunDisc({ cx, cy, r, glow = 0.5, idPrefix = 'sun' }) {
  const stop = (o, a) => ({ stopColor: 'var(--trodo-primary)', stopOpacity: a })
  return (
    <g aria-hidden="true">
      <defs>
        <radialGradient id={`${idPrefix}-glow`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" style={stop(0, glow)} />
          <stop offset="0.35" style={stop(0, glow * 0.45)} />
          <stop offset="0.7" style={stop(0, glow * 0.12)} />
          <stop offset="1" style={stop(0, 0)} />
        </radialGradient>
        <radialGradient id={`${idPrefix}-disc`} cx="0.5" cy="0.38" r="0.62">
          <stop offset="0" stopColor="#fff" stopOpacity="0.55" />
          <stop offset="0.45" stopColor="#fff" stopOpacity="0.08" />
          <stop offset="1" stopColor="#000" stopOpacity="0.12" />
        </radialGradient>
      </defs>
      {glow > 0 && <circle cx={cx} cy={cy} r={r * 4.2} fill={`url(#${idPrefix}-glow)`} />}
      <circle cx={cx} cy={cy} r={r} style={{ fill: 'var(--trodo-primary)' }} />
      <circle cx={cx} cy={cy} r={r} fill={`url(#${idPrefix}-disc)`} />
    </g>
  )
}

const LAYERS = [
  // far → near. Far ridges are warm, the light is behind them; near ones cool and darker.
  { seed: 13, base: 0.6, amp: 0.3, scale: 0.0018, fill: '#e7e2db', sharp: 2.6, oct: 3, tilt: -0.02 },
  { seed: 23, base: 0.7, amp: 0.22, scale: 0.0026, fill: '#d9d5ce', sharp: 2.0, oct: 3, tilt: -0.02 },
  { seed: 37, base: 0.8, amp: 0.16, scale: 0.0036, fill: '#c8c6c0', sharp: 1.5, oct: 3, tilt: 0 },
  { seed: 51, base: 0.9, amp: 0.11, scale: 0.0048, fill: '#b1afa9', sharp: 1.2, oct: 2, tilt: 0.02 },
]

/*
 * Mist that drifts sideways between ridge layers, keyed by the layer it sits in front
 * of. [cx, cy, rx, ry, opacity] as fractions of the canvas. The bands run wider than
 * the canvas so the drift never shows an edge.
 */
const MIST = {
  0: [
    [0.18, 0.66, 0.34, 0.04, 0.6],
    [0.78, 0.63, 0.3, 0.035, 0.55],
  ],
  1: [
    [0.45, 0.77, 0.42, 0.035, 0.5],
    [1.05, 0.75, 0.3, 0.032, 0.45],
  ],
  2: [[0.3, 0.87, 0.4, 0.032, 0.4]],
}

export function Ridge({ width = 1600, height = 560, className, sun }) {
  const step = 6
  const paths = LAYERS.map((L) => {
    const noise = makeNoise(L.seed)
    const pts = []
    for (let x = 0; x <= width; x += step) {
      const h = ridgeHeight(noise, x * L.scale, L.oct, L.sharp)
      const y = height * L.base - h * height * L.amp + (x / width - 0.5) * height * L.tilt
      pts.push(`${x},${y.toFixed(1)}`)
    }
    // runs well below the canvas, so a ridge lifted by the scroll never shows its foot
    return `M0,${height * 2} L${pts.join(' L')} L${width},${height * 2} Z`
  })

  return (
    <svg
      className={className}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMax slice"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="rg-haze" cx="0.62" cy="0.7" r="0.7">
          <stop offset="0" stopColor="#f3c9ad" stopOpacity="0.4" />
          <stop offset="0.5" stopColor="#f3d6c2" stopOpacity="0.12" />
          <stop offset="1" stopColor="#f4f4f2" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="rg-mist" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f4f4f2" stopOpacity="1" />
          <stop offset="0.45" stopColor="#f4f4f2" stopOpacity="0.55" />
          <stop offset="1" stopColor="#f4f4f2" stopOpacity="0" />
        </linearGradient>
        <radialGradient id="rg-sun" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#fbf3ea" stopOpacity="1" />
          <stop offset="0.3" stopColor="#faf0e5" stopOpacity="0.97" />
          <stop offset="0.55" stopColor="#f9ecdf" stopOpacity="0.9" />
          <stop offset="0.75" stopColor="#f7e3d2" stopOpacity="0.5" />
          <stop offset="0.9" stopColor="#f6dcc6" stopOpacity="0.15" />
          <stop offset="1" stopColor="#f6dcc6" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="rg-core" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#f9f2e9" stopOpacity="1" />
          <stop offset="0.45" stopColor="#f8f1e8" stopOpacity="0.9" />
          <stop offset="0.75" stopColor="#f8f1e8" stopOpacity="0.4" />
          <stop offset="1" stopColor="#f8f1e8" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="rg-band" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#f7f5f1" stopOpacity="0.85" />
          <stop offset="0.6" stopColor="#f7f5f1" stopOpacity="0.35" />
          <stop offset="1" stopColor="#f7f5f1" stopOpacity="0" />
        </radialGradient>
        <filter id="rg-grain">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" />
          <feColorMatrix type="saturate" values="0" />
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.5" intercept="-0.1" />
          </feComponentTransfer>
        </filter>
      </defs>

      {!sun && <rect width={width} height={height} fill="url(#rg-haze)" />}
      {sun ? <SunDisc {...sun} idPrefix="rg-sun" /> : (
        <g className="rg-sun">
          <circle cx={width * 0.62} cy={height * 0.56} r={height * 0.3} fill="url(#rg-sun)" opacity="0.9" />
          <circle cx={width * 0.62} cy={height * 0.56} r={height * 0.12} fill="url(#rg-core)" />
        </g>
      )}

      {paths.map((d, i) => (
        <g key={i} className="rg-layer" style={{ '--k': i }}>
          <path d={d} fill={LAYERS[i].fill} />
          {i < LAYERS.length - 1 && <path d={d} fill="url(#rg-mist)" opacity={0.42 - i * 0.12} />}
          {MIST[i] && (
            <g className={`rg-drift rg-drift--${i}`}>
              {MIST[i].map(([cx, cy, rx, ry, o], k) => (
                <ellipse
                  key={k}
                  cx={width * cx}
                  cy={height * cy}
                  rx={width * rx}
                  ry={height * ry}
                  fill="url(#rg-band)"
                  opacity={o}
                />
              ))}
            </g>
          )}
        </g>
      ))}



      {!sun && <rect
        width={width}
        height={height}
        filter="url(#rg-grain)"
        opacity="0.18"
        style={{ mixBlendMode: 'multiply' }}
      />}
    </svg>
  )
}
